import type {
  BodyContext,
  CarrierSource,
  CyclePhase,
  CycleType,
  DataAvailabilitySummary,
  EggSource,
  ExperienceContext,
  ExperienceDevDataProfile,
  FamilyBuildingPlanContext,
  JourneyStage,
  LifecycleContext,
  LifecycleStage,
  MaterialContext,
  MetricSummary,
  OutcomeState,
  PartnerChoice,
  PrimaryIntent,
  ProcessContext,
  SexAtBirth,
  SpermSource,
  TaskSummary,
  TreatmentContext,
  TreatmentGoal,
  TreatmentModality,
  ViewerContext,
  ViewerRole,
} from "./experience.types";

declare const __DEV__: boolean | undefined;

export type ExperienceContextInput = {
  viewer?: Partial<ViewerContext>;
  familyBuildingPlan?: Partial<FamilyBuildingPlanContext>;
  lifecycle?: Partial<LifecycleContext>;
  treatment?: Partial<TreatmentContext>;
  bodyContexts?: BodyContext[];
  materialContexts?: MaterialContext[];
  processContexts?: ProcessContext[];
  tasks?: TaskSummary[];
  metrics?: Partial<MetricSummary>;
  dataAvailability?: Partial<DataAvailabilitySummary>;
  dev?: {
    isOverride?: boolean;
    dataProfile?: ExperienceDevDataProfile;
  };
};

export type ExperienceDevOverrides = {
  sexAtBirth?: SexAtBirth;
  primaryIntent?: PrimaryIntent;
  treatmentGoal?: TreatmentGoal;
  journeyStage?: JourneyStage;
  partnerChoice?: PartnerChoice;
  viewerRole?: ViewerRole;
  modality?: TreatmentModality;
  cycleType?: CycleType;
  currentPhase?: CyclePhase;
  hasActiveCycle?: boolean;
  outcome?: OutcomeState;
  dataProfile?: ExperienceDevDataProfile;
};

const DEFAULT_DATA_AVAILABILITY: DataAvailabilitySummary = {
  profile: "missing",
  tasks: "missing",
  schedule: "missing",
  monitoring: "missing",
  semen: "missing",
  embryology: "missing",
  cryoInventory: "missing",
  money: "missing",
};

const DEFAULT_METRICS: MetricSummary = {
  hasMonitoringSummary: false,
  hasSemenSummary: false,
  hasEmbryologySummary: false,
  hasCryoInventory: false,
  hasMoneySummary: false,
};

function unique<T extends string>(values: T[]): T[] {
  return [...new Set(values)];
}

function normalizeGoal(goal: string | null | undefined): TreatmentGoal {
  switch (goal) {
    case "egg-freezing":
    case "egg-preserve":
    case "preserve":
      return "egg-freezing";
    case "sperm-freezing":
    case "sperm-preserve":
      return "sperm-freezing";
    case "embryo-freezing":
    case "embryo-preserve":
      return "embryo-freezing";
    case "pregnancy":
    case "ivf":
    case "iui":
    case "fet":
    case "egg-donation":
    case "sperm-donation":
    case "surrogacy":
    case "adoption":
    case "exploring":
      return goal;
    default:
      return "unknown";
  }
}

function normalizeSexAtBirth(value: string | null | undefined): SexAtBirth {
  if (value === "AFAB" || value === "female") return "AFAB";
  if (value === "AMAB" || value === "male") return "AMAB";
  return "unknown";
}

function normalizeDevGoal(
  goal: string | null | undefined,
  sexAtBirth: SexAtBirth | undefined,
): TreatmentGoal | undefined {
  if (!goal) return undefined;
  if (goal === "preserve") {
    return sexAtBirth === "AMAB" ? "sperm-freezing" : "egg-freezing";
  }
  return normalizeGoal(goal);
}

function derivePrimaryIntent(goal: TreatmentGoal): PrimaryIntent {
  switch (goal) {
    case "egg-freezing":
    case "sperm-freezing":
    case "embryo-freezing":
      return "preserve";
    case "pregnancy":
    case "ivf":
    case "iui":
    case "fet":
    case "surrogacy":
    case "adoption":
      return "grow";
    case "egg-donation":
    case "sperm-donation":
      return "donate";
    case "exploring":
      return "learn";
    default:
      return "unknown";
  }
}

/**
 * Whether her latest declared treatment is something other than preservation.
 *
 * Intent is deliberately not consulted. It is the answer that routed her
 * onboarding, not what she is doing now; a patient who came in to freeze eggs
 * and later chose IVF has moved journeys while her intent still reads
 * "preserve", and that is normal history, not a contradiction. Treatment moves
 * the journey; intent does not.
 */
function hasMovedOnFromPreservation(args: {
  goal: TreatmentGoal;
  modality: TreatmentModality;
}): boolean {
  if (args.modality !== "unknown") return true;
  switch (args.goal) {
    case "pregnancy":
    case "ivf":
    case "iui":
    case "fet":
    case "surrogacy":
    case "adoption":
      return true;
    default:
      return false;
  }
}

function deriveLifecycleStage(args: {
  journeyStage: JourneyStage;
  hasActiveCycle: boolean;
  outcome: OutcomeState;
  goal: TreatmentGoal;
  modality: TreatmentModality;
  hasCompletedPreservationCycle: boolean;
}): LifecycleStage {
  // An open cycle is active care right up until it has a result to report.
  // Recording a result deliberately does not close the cycle, so "there is an
  // open cycle" can no longer be read as "there is nothing to report yet" —
  // that inference is what left a patient with a positive beta on an open cycle
  // with no result surface at all. A cycle with no recorded ending resolves to
  // `"none"` and still reaches `active-care` here, unchanged.
  if (args.hasActiveCycle && args.outcome === "none") return "active-care";
  if (args.outcome !== "none") return "results";
  if (args.journeyStage === "time-to-wait") return "waiting";
  if (args.journeyStage === "complete") {
    if (
      args.goal === "egg-freezing" ||
      args.goal === "sperm-freezing" ||
      args.goal === "embryo-freezing"
    ) {
      return "storage";
    }
    return "results";
  }
  if (
    args.journeyStage === "screening" ||
    args.journeyStage === "matched" ||
    args.journeyStage === "approved"
  ) {
    return "planning";
  }
  if (args.journeyStage === "donating" || args.journeyStage === "stimulation") {
    return "active-care";
  }
  /**
   * A preservation cycle she has already been through, when no stage says so.
   *
   * Last of the fall-throughs on purpose, and the placement is the whole point.
   *
   * Above `time-to-wait` it stole the wait from a sperm-freezing patient whose
   * analysis had not come back — she has banked a sample *and* is waiting, and
   * waiting is the truer statement. Above the donor arms it demoted a donor
   * mid-donation to storage, because a donation cycle is a preservation cycle
   * clinically. Here, it only answers for someone no other rule has an answer
   * for, which is exactly the patient this exists for: finished, and otherwise
   * indistinguishable from someone who never started.
   */
  /**
   * Still preserving: she has been through a preservation cycle and nothing she
   * has declared since says she is now pursuing a different treatment.
   *
   * The gate exists because a patient who banked eggs and then moved her plan
   * to IVF was parked on an egg-freezing post-cycle home with no recency bound
   * to release her: having stored eggs is the subject only while storing is
   * still what she is doing. What releases her is a later treatment answer —
   * an accepted plan, a planned modality, or a conception goal — never the
   * onboarding intent (see `hasMovedOnFromPreservation`).
   */
  if (
    args.hasCompletedPreservationCycle &&
    !hasMovedOnFromPreservation({ goal: args.goal, modality: args.modality })
  ) {
    return "storage";
  }
  if (args.journeyStage === "unknown") return "setup";
  return "planning";
}

function deriveBodyContexts(args: {
  sexAtBirth: SexAtBirth;
  viewerRole: ViewerRole;
  goal: TreatmentGoal;
  cycleType: CycleType;
  partnerChoice: PartnerChoice;
}): BodyContext[] {
  const contexts: BodyContext[] = [];

  if (
    args.goal === "egg-freezing" ||
    args.goal === "embryo-freezing" ||
    args.goal === "egg-donation" ||
    args.cycleType === "egg_freezing" ||
    args.cycleType === "embryo_freezing" ||
    args.cycleType === "ivf" ||
    args.cycleType === "icsi"
  ) {
    if (args.sexAtBirth === "AFAB" || args.viewerRole === "donor") {
      contexts.push("ovarian");
    }
  }

  if (
    args.goal === "sperm-freezing" ||
    args.goal === "sperm-donation" ||
    args.sexAtBirth === "AMAB" ||
    args.cycleType === "sperm_freezing"
  ) {
    contexts.push("sperm-production");
  }

  if (
    args.goal === "pregnancy" ||
    args.goal === "iui" ||
    args.goal === "fet" ||
    args.cycleType === "iui" ||
    args.cycleType === "fet" ||
    args.partnerChoice === "with-surrogate"
  ) {
    if (args.sexAtBirth === "AFAB" || args.viewerRole === "carrier") {
      contexts.push("uterine-gestational");
    }
  }

  contexts.push("general-health");
  return unique(contexts.length > 0 ? contexts : ["none"]);
}

function deriveMaterialContexts(args: {
  goal: TreatmentGoal;
  cycleType: CycleType;
  modality: TreatmentModality;
  lifecycleStage: LifecycleStage;
  hasCryoInventory: boolean;
  eggSource: EggSource;
  spermSource: SpermSource;
}): MaterialContext[] {
  const contexts: MaterialContext[] = [];

  if (
    args.goal === "egg-freezing" ||
    args.goal === "embryo-freezing" ||
    args.goal === "egg-donation" ||
    (args.eggSource !== "none" && args.eggSource !== "unknown") ||
    args.cycleType === "egg_freezing" ||
    args.cycleType === "embryo_freezing" ||
    args.cycleType === "ivf" ||
    args.cycleType === "icsi"
  ) {
    contexts.push("eggs");
  }

  if (
    args.goal === "sperm-freezing" ||
    args.goal === "sperm-donation" ||
    args.goal === "embryo-freezing" ||
    args.goal === "pregnancy" ||
    (args.spermSource !== "none" && args.spermSource !== "unknown") ||
    args.cycleType === "sperm_freezing" ||
    args.cycleType === "ivf" ||
    args.cycleType === "icsi" ||
    args.cycleType === "iui"
  ) {
    contexts.push("sperm");
  }

  if (
    args.goal === "embryo-freezing" ||
    args.cycleType === "embryo_freezing" ||
    args.cycleType === "ivf" ||
    args.cycleType === "icsi" ||
    args.cycleType === "fet" ||
    args.modality === "ivf" ||
    args.modality === "icsi" ||
    args.modality === "fet"
  ) {
    contexts.push("embryos");
  }

  if (args.lifecycleStage === "storage" || args.hasCryoInventory) {
    contexts.push("stored-specimens");
  }

  return unique(contexts.length > 0 ? contexts : ["none"]);
}

function processFromPhase(phase: CyclePhase): ProcessContext[] {
  switch (phase) {
    case "protocol":
    case "stimulation":
      return ["stimulation"];
    case "trigger":
    case "trigger_shot":
      return ["trigger"];
    case "egg_retrieval":
      return ["retrieval"];
    case "sperm_collection":
      return ["collection"];
    case "insemination":
      return ["insemination"];
    case "fertilization":
      return ["fertilization"];
    case "embryo_development":
      return ["embryo-culture"];
    case "transfer":
      return ["transfer"];
    case "luteal_phase":
      return ["waiting"];
    case "freezing":
      return ["storage"];
    default:
      return [];
  }
}

function deriveProcessContexts(args: {
  goal: TreatmentGoal;
  lifecycleStage: LifecycleStage;
  currentPhase: CyclePhase;
  cycleType: CycleType;
  modality: TreatmentModality;
  partnerChoice: PartnerChoice;
  viewerRole: ViewerRole;
}): ProcessContext[] {
  const contexts: ProcessContext[] = [];
  contexts.push(...processFromPhase(args.currentPhase));

  if (args.lifecycleStage === "planning") {
    contexts.push("testing", "optimization", "matching");
  }

  if (args.lifecycleStage === "waiting") contexts.push("waiting");
  if (args.lifecycleStage === "storage") contexts.push("storage");

  if (
    args.goal === "embryo-freezing" ||
    args.cycleType === "embryo_freezing" ||
    args.cycleType === "ivf" ||
    args.cycleType === "icsi"
  ) {
    contexts.push("fertilization", "embryo-culture");
  }

  if (
    args.cycleType === "fet" ||
    args.cycleType === "ivf" ||
    args.cycleType === "icsi" ||
    args.modality === "fet" ||
    args.modality === "ivf" ||
    args.modality === "icsi"
  ) {
    contexts.push("transfer");
  }

  if (
    args.partnerChoice === "using-donor" ||
    args.partnerChoice === "with-surrogate" ||
    // The surrogacy goal describes the intended parent's plan. A carrier
    // can share the same goal value in onboarding, but should not enter legal
    // review until the relationship resolver says that review has started.
    (args.goal === "surrogacy" && args.viewerRole !== "carrier")
  ) {
    contexts.push("legal", "matching");
  }

  return unique(contexts.length > 0 ? contexts : ["testing"]);
}

function deriveSources(args: {
  goal: TreatmentGoal;
  sexAtBirth: SexAtBirth;
  partnerChoice: PartnerChoice;
}): Pick<
  FamilyBuildingPlanContext,
  "eggSource" | "spermSource" | "carrierSource"
> {
  let eggSource: EggSource = "unknown";
  let spermSource: SpermSource = "unknown";
  let carrierSource: CarrierSource = "unknown";

  if (args.goal === "egg-freezing" || args.goal === "egg-donation") {
    eggSource = "self";
    spermSource = "none";
    carrierSource = "none";
  }

  if (args.goal === "sperm-freezing" || args.goal === "sperm-donation") {
    eggSource = "none";
    spermSource = "self";
    carrierSource = "none";
  }

  if (args.goal === "embryo-freezing" || args.goal === "pregnancy") {
    eggSource = args.sexAtBirth === "AFAB" ? "self" : "unknown";
    spermSource = args.sexAtBirth === "AMAB" ? "self" : "unknown";
    carrierSource = args.sexAtBirth === "AFAB" ? "self" : "unknown";

    if (args.partnerChoice === "with-partner") {
      if (args.sexAtBirth === "AFAB") spermSource = "partner";
      if (args.sexAtBirth === "AMAB") {
        eggSource = "partner";
        carrierSource = "partner";
      }
    }
    if (args.partnerChoice === "using-donor") {
      if (args.sexAtBirth === "AFAB") spermSource = "donor";
      if (args.sexAtBirth === "AMAB") eggSource = "donor";
    }
    if (args.partnerChoice === "with-surrogate") {
      carrierSource = "gestational-carrier";
    }
  }

  return { eggSource, spermSource, carrierSource };
}

export function resolveExperienceContext(
  input: ExperienceContextInput,
): ExperienceContext {
  const goal = normalizeGoal(input.familyBuildingPlan?.treatmentGoal);
  const primaryIntent =
    input.familyBuildingPlan?.primaryIntent ?? derivePrimaryIntent(goal);
  const sexAtBirth = input.viewer?.sexAtBirth ?? "unknown";
  const role = input.viewer?.role ?? "primary";
  const partnerChoice = input.familyBuildingPlan?.partnerChoice ?? "unknown";
  const cycleType = input.treatment?.cycleType ?? "unknown";
  const currentPhase = input.treatment?.currentPhase ?? "unknown";
  const modality = input.familyBuildingPlan?.modality ?? "unknown";
  const outcome = input.lifecycle?.outcome ?? "none";
  const hasActiveCycle = input.treatment?.hasActiveCycle ?? false;
  const hasCompletedPreservationCycle =
    input.treatment?.hasCompletedPreservationCycle ?? false;
  const awaitingSemenAnalysis = input.treatment?.awaitingSemenAnalysis ?? false;
  const spermSampleCollected = input.treatment?.spermSampleCollected ?? false;
  const journeyStage = input.lifecycle?.journeyStage ?? "unknown";
  const metrics: MetricSummary = {
    ...DEFAULT_METRICS,
    ...input.metrics,
  };
  const lifecycleStage =
    input.lifecycle?.stage ??
    deriveLifecycleStage({
      journeyStage,
      hasActiveCycle,
      outcome,
      goal,
      modality,
      hasCompletedPreservationCycle,
    });
  const sources = deriveSources({
    goal,
    sexAtBirth,
    partnerChoice,
  });
  const sourceContext = {
    eggSource: input.familyBuildingPlan?.eggSource ?? sources.eggSource,
    spermSource: input.familyBuildingPlan?.spermSource ?? sources.spermSource,
    carrierSource:
      input.familyBuildingPlan?.carrierSource ?? sources.carrierSource,
  };

  return {
    viewer: {
      role,
      sexAtBirth,
      isPrimary: input.viewer?.isPrimary ?? role === "primary",
      partnerConnected: input.viewer?.partnerConnected ?? false,
    },
    bodyContexts:
      input.bodyContexts ??
      deriveBodyContexts({
        sexAtBirth,
        viewerRole: role,
        goal,
        cycleType,
        partnerChoice,
      }),
    materialContexts:
      input.materialContexts ??
      deriveMaterialContexts({
        goal,
        cycleType,
        modality,
        lifecycleStage,
        hasCryoInventory: metrics.hasCryoInventory,
        eggSource: sourceContext.eggSource,
        spermSource: sourceContext.spermSource,
      }),
    processContexts:
      input.processContexts ??
      deriveProcessContexts({
        goal,
        lifecycleStage,
        currentPhase,
        cycleType,
        modality,
        partnerChoice,
        viewerRole: role,
      }),
    familyBuildingPlan: {
      primaryIntent,
      treatmentGoal: goal,
      partnerChoice,
      modality,
      ...sourceContext,
    },
    lifecycle: {
      stage: lifecycleStage,
      journeyStage,
      outcome,
    },
    treatment: {
      hasActiveCycle,
      cycleType,
      currentPhase,
      cycleStatus: input.treatment?.cycleStatus ?? "unknown",
      hasCompletedPreservationCycle,
      awaitingSemenAnalysis,
      spermSampleCollected,
    },
    tasks: input.tasks ?? [],
    metrics,
    dataAvailability: {
      ...DEFAULT_DATA_AVAILABILITY,
      ...input.dataAvailability,
    },
    dev: {
      isOverride: input.dev?.isOverride ?? false,
      dataProfile: input.dev?.dataProfile ?? "none",
    },
  };
}

function getParam(params: URLSearchParams, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = params.get(key);
    if (value) return value;
  }
  return undefined;
}

export function readExperienceDevOverrides(
  search?: string,
): ExperienceDevOverrides {
  const devFlag = typeof __DEV__ !== "undefined" ? __DEV__ : undefined;
  if (devFlag === false) return {};

  const rawSearch =
    search ??
    (typeof window !== "undefined" ? window.location.search : undefined);
  if (!rawSearch) return {};

  const params = new URLSearchParams(rawSearch);
  const sexAtBirth = normalizeSexAtBirth(
    getParam(params, ["dev_gender", "force_gender"]),
  );
  const rawTreatmentGoal = getParam(params, [
    "dev_treatmentGoal",
    "force_goal",
  ]);
  const forceActiveCycle = params.get("dev_force_active_cycle");

  return {
    sexAtBirth: sexAtBirth === "unknown" ? undefined : sexAtBirth,
    primaryIntent: getParam(params, ["dev_primaryIntent"]) as
      PrimaryIntent | undefined,
    treatmentGoal: normalizeDevGoal(rawTreatmentGoal, sexAtBirth),
    journeyStage: getParam(params, ["dev_stage", "force_stage"]) as
      JourneyStage | undefined,
    partnerChoice: getParam(params, ["dev_partnerChoice"]) as
      PartnerChoice | undefined,
    viewerRole: getParam(params, ["dev_viewerRole"]) as ViewerRole | undefined,
    modality: getParam(params, ["dev_modality"]) as
      TreatmentModality | undefined,
    cycleType: getParam(params, ["dev_cycleType"]) as CycleType | undefined,
    currentPhase: getParam(params, ["dev_cyclePhase", "dev_currentPhase"]) as
      CyclePhase | undefined,
    hasActiveCycle:
      forceActiveCycle === "true"
        ? true
        : forceActiveCycle === "false"
          ? false
          : undefined,
    outcome: getParam(params, ["dev_outcome"]) as OutcomeState | undefined,
    dataProfile: getParam(params, ["dev_dataProfile"]) as
      ExperienceDevDataProfile | undefined,
  };
}

export function applyExperienceDevOverrides(
  input: ExperienceContextInput,
  overrides: ExperienceDevOverrides,
): ExperienceContextInput {
  const hasOverride = Object.values(overrides).some(Boolean);
  if (!hasOverride) return input;

  return {
    ...input,
    viewer: {
      ...input.viewer,
      sexAtBirth: overrides.sexAtBirth ?? input.viewer?.sexAtBirth,
      role: overrides.viewerRole ?? input.viewer?.role,
    },
    familyBuildingPlan: {
      ...input.familyBuildingPlan,
      primaryIntent:
        overrides.primaryIntent ?? input.familyBuildingPlan?.primaryIntent,
      treatmentGoal:
        overrides.treatmentGoal ?? input.familyBuildingPlan?.treatmentGoal,
      partnerChoice:
        overrides.partnerChoice ?? input.familyBuildingPlan?.partnerChoice,
      modality: overrides.modality ?? input.familyBuildingPlan?.modality,
    },
    lifecycle: {
      ...input.lifecycle,
      journeyStage: overrides.journeyStage ?? input.lifecycle?.journeyStage,
      outcome: overrides.outcome ?? input.lifecycle?.outcome,
    },
    treatment: {
      ...input.treatment,
      cycleType: overrides.cycleType ?? input.treatment?.cycleType,
      currentPhase: overrides.currentPhase ?? input.treatment?.currentPhase,
      hasActiveCycle:
        overrides.hasActiveCycle ??
        input.treatment?.hasActiveCycle ??
        Boolean(overrides.cycleType || overrides.currentPhase),
    },
    dev: {
      ...input.dev,
      isOverride: true,
      dataProfile: overrides.dataProfile ?? input.dev?.dataProfile ?? "none",
    },
  };
}
